import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { maintenanceApi } from "../../api/maintenanceApi";
import type { MaintenanceDashboard } from "../../api/maintenanceApi";

export default function MaintenanceAlertsByMachineWidget() {
  const { token } = useAuth() as { token: string | null };
  const [data, setData] = useState<MaintenanceDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    maintenanceApi
      .getDashboard(token)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="card h-100">
        <div className="card-header fw-semibold">Avvisi manutenzione macchine</div>
        <div className="card-body text-center py-4">
          <div className="spinner-border spinner-border-sm text-muted" />
        </div>
      </div>
    );
  }

  // Combina work orders aperti e piani in scadenza (BY_DATE e BY_CYCLES)
  const alerts: Array<{
    machineId: string;
    machineName: string;
    type: "work_order" | "plan_due";
    priority: string;
    message: string;
    daysUntilDue?: number;
    cyclesUntilDue?: number;
    severity?: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
  }> = [];

  // Work orders aperti
  data?.openWorkOrders?.forEach((wo) => {
    alerts.push({
      machineId: wo.machineId,
      machineName: wo.machine?.name || "Macchina sconosciuta",
      type: "work_order",
      priority: wo.priority,
      message: wo.title,
    });
  });

  // Piani in scadenza (BY_DATE: giorni, BY_CYCLES: cicli - già filtrati dal backend)
  data?.plansDueSoon?.forEach(({ plan, daysUntilDue, cyclesUntilDue, severity }) => {
    alerts.push({
      machineId: plan.machineId,
      machineName: plan.machine?.name || "Macchina sconosciuta",
      type: "plan_due",
      priority: severity ?? plan.priority,
      message: plan.name,
      daysUntilDue: daysUntilDue ?? undefined,
      cyclesUntilDue: cyclesUntilDue ?? undefined,
      severity: severity ?? undefined,
    });
  });

  // Raggruppa per macchina
  const alertsByMachine = new Map<string, typeof alerts>();
  alerts.forEach((alert) => {
    if (!alertsByMachine.has(alert.machineId)) {
      alertsByMachine.set(alert.machineId, []);
    }
    alertsByMachine.get(alert.machineId)!.push(alert);
  });

  if (alertsByMachine.size === 0) {
    return (
      <div className="card h-100">
        <div className="card-header fw-semibold d-flex align-items-center justify-content-between">
          <span>Avvisi manutenzione macchine</span>
          <span className="badge bg-success">Tutto OK</span>
        </div>
        <div className="card-body text-center py-4 text-muted">
          <i className="bi bi-check-circle-fill fs-3 mb-2 d-block text-success" />
          Nessuna manutenzione richiesta
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100">
      <div className="card-header fw-semibold d-flex align-items-center justify-content-between">
        <span>⚠️ Manutenzione richiesta</span>
        <span className="badge bg-danger">{alertsByMachine.size}</span>
      </div>
      <div className="card-body p-0">
        <div className="list-group list-group-flush">
          {Array.from(alertsByMachine.entries()).map(([machineId, machineAlerts]) => {
            const machineName = machineAlerts[0]?.machineName || "Macchina sconosciuta";
            const isUrgent = machineAlerts.some((a) => {
              if (a.daysUntilDue !== undefined && a.daysUntilDue <= 0) return true;
              if (a.cyclesUntilDue !== undefined && a.cyclesUntilDue <= 0) return true;
              return false;
            });
            const highestPriority = machineAlerts.reduce((max, a) => {
              const priorityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
              const pA = a.severity ?? a.priority;
              const pMax = max.severity ?? max.priority;
              return priorityOrder[pA as keyof typeof priorityOrder] >
                priorityOrder[pMax as keyof typeof priorityOrder]
                ? a
                : max;
            }, machineAlerts[0]);

            return (
              <div
                key={machineId}
                className={`list-group-item maintenance-alert-item ${
                  isUrgent ? "maintenance-alert-urgent" : ""
                }`}
              >
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <strong className="maintenance-alert-machine-name">{machineName}</strong>
                      <span
                        className={`badge ${
                          (highestPriority.severity ?? highestPriority.priority) === "CRITICAL"
                            ? "bg-danger"
                            : (highestPriority.severity ?? highestPriority.priority) === "HIGH"
                            ? "bg-warning"
                            : (highestPriority.severity ?? highestPriority.priority) === "MEDIUM"
                            ? "bg-info"
                            : "bg-secondary"
                        }`}
                      >
                        {highestPriority.severity ?? highestPriority.priority}
                      </span>
                      {isUrgent && (
                        <span className="badge bg-danger">IN RITARDO</span>
                      )}
                    </div>
                    <div className="small text-muted">
                      {machineAlerts.length === 1 ? (
                        <span>{machineAlerts[0].message}</span>
                      ) : (
                        <span>{machineAlerts.length} interventi richiesti</span>
                      )}
                      {machineAlerts.some(
                        (a) => a.daysUntilDue !== undefined || a.cyclesUntilDue !== undefined
                      ) && (
                        <span className="ms-2">
                          {machineAlerts
                            .filter((a) => a.daysUntilDue !== undefined || a.cyclesUntilDue !== undefined)
                            .map((a) => {
                              if (a.daysUntilDue !== undefined) {
                                if (a.daysUntilDue <= 0) return `In ritardo di ${Math.abs(a.daysUntilDue)} gg`;
                                return `Tra ${a.daysUntilDue} gg`;
                              }
                              if (a.cyclesUntilDue !== undefined) {
                                if (a.cyclesUntilDue <= 0)
                                  return `In ritardo di ${Math.abs(a.cyclesUntilDue)} cicli`;
                                return `Tra ${a.cyclesUntilDue} cicli`;
                              }
                              return null;
                            })
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/maintenance"
                    className="btn btn-sm btn-warning maintenance-alert-button"
                    title="Vai alla pagina manutenzione"
                  >
                    <i className="bi bi-wrench-adjustable-circle me-1" />
                    Manutenzione
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

